import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CreditCard, TrendingDown, ArrowRight } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { UnifiedInvoiceAPI } from '@/services/invoicing';
import { UnifiedInvoice, AvailableForApplication } from '@/types/invoicing';
import { formatCurrency } from '@/utils/document';

interface ApplyToInvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: UnifiedInvoice | null;
  onApplicationComplete: () => void;
}

export function ApplyToInvoiceDialog({
  isOpen,
  onClose,
  invoice,
  onApplicationComplete,
}: ApplyToInvoiceDialogProps) {
  const [availableItems, setAvailableItems] = useState<AvailableForApplication[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen && invoice) {
      fetchAvailable();
      setSelectedSourceId(null);
      setAmount('');
      setNotes('');
    }
  }, [isOpen, invoice?.id]);

  const fetchAvailable = async () => {
    if (!invoice) return;
    setIsLoading(true);
    try {
      const items = await UnifiedInvoiceAPI.getAvailableForApplication(invoice.id);
      setAvailableItems(items);
    } catch (error) {
      console.error('Failed to fetch available items:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch available credit notes and down payments',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedItem = availableItems.find(i => i.id === selectedSourceId);

  const maxAmount = selectedItem
    ? Math.min(
        Number(selectedItem.remaining_amount),
        Number(invoice?.balance_due ?? invoice?.total ?? 0)
      )
    : 0;

  const handleApply = async () => {
    if (!invoice || !selectedSourceId) return;

    setIsApplying(true);
    try {
      await UnifiedInvoiceAPI.applyToInvoice(
        selectedSourceId,
        invoice.id,
        amount ? Number(amount) : undefined,
        notes || undefined
      );

      toast({
        title: 'Application successful',
        description: `${selectedItem?.invoice_type === 'credit_note' ? 'Credit note' : 'Down payment'} applied to ${invoice.invoice_number}`,
      });

      onApplicationComplete();
      onClose();
    } catch (error: any) {
      console.error('Failed to apply:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.detail || 'Failed to apply';
      toast({
        title: 'Error',
        description: typeof errorMessage === 'string' ? errorMessage : 'Failed to apply to invoice',
        variant: 'destructive',
      });
    } finally {
      setIsApplying(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Apply to {invoice.invoice_number}</DialogTitle>
          <DialogDescription>
            Apply a credit note or down payment to reduce the balance due ({formatCurrency(invoice.balance_due ?? invoice.total)}).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
            </div>
          ) : availableItems.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <p>No credit notes or down payments available for this invoice.</p>
              <p className="text-xs mt-1">They must belong to the same client and have remaining balance.</p>
            </div>
          ) : (
            <>
              {/* Available items list */}
              <div>
                <Label className="text-sm font-medium">Select source to apply</Label>
                <ScrollArea className="h-[200px] mt-2">
                  <div className="space-y-2">
                    {availableItems.map((item) => (
                      <div
                        key={item.id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedSourceId === item.id
                            ? 'border-primary bg-primary/5'
                            : 'hover:bg-muted/50'
                        }`}
                        onClick={() => {
                          setSelectedSourceId(item.id);
                          // Auto-fill with max applicable amount
                          const max = Math.min(
                            Number(item.remaining_amount),
                            Number(invoice.balance_due ?? invoice.total ?? 0)
                          );
                          setAmount(max.toFixed(2));
                        }}
                      >
                        <div className="flex items-center gap-3">
                          {item.invoice_type === 'credit_note' ? (
                            <CreditCard className="h-4 w-4 text-destructive" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-blue-500" />
                          )}
                          <div>
                            <div className="font-medium text-sm">{item.invoice_number}</div>
                            <div className="text-xs text-muted-foreground">
                              {item.invoice_type === 'credit_note' ? 'Credit Note' : 'Down Payment'}
                              {' • '}
                              {item.issue_date}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-sm">{formatCurrency(item.remaining_amount)}</div>
                          <div className="text-xs text-muted-foreground">remaining</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {selectedSourceId && (
                <>
                  <Separator />

                  {/* Application preview */}
                  <div className="flex items-center justify-center gap-3 text-sm">
                    <Badge variant={selectedItem?.invoice_type === 'credit_note' ? 'destructive' : 'secondary'}>
                      {selectedItem?.invoice_number}
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="default">{invoice.invoice_number}</Badge>
                  </div>

                  <div>
                    <Label htmlFor="apply-amount">
                      Amount to apply
                      <span className="text-xs text-muted-foreground ml-1">(max: {formatCurrency(maxAmount)})</span>
                    </Label>
                    <Input
                      id="apply-amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={maxAmount}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={maxAmount.toFixed(2)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Leave empty to apply the maximum possible amount.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="apply-notes">Notes (optional)</Label>
                    <Textarea
                      id="apply-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      placeholder="Add a note about this application..."
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isApplying}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={isApplying || !selectedSourceId}
          >
            {isApplying ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent mr-2" />
                Applying...
              </>
            ) : (
              <>Apply {amount ? formatCurrency(amount) : 'Amount'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
